import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import LeadsForm from '../Leads/components/LeadsForm';
// import OverviewView from './components/OverviewView';
import AdvancedFilterPanel, { FilterButton } from '../../components/common/AdvancedFilter';
import FilterAwarenessBar from '../../components/common/AdvancedFilter/FilterAwarenessBar';
import { getActivePeriodRange, getActiveFilterCount } from '../../components/common/AdvancedFilter/periodUtils';
import { useLeadsForm } from '../Leads/hooks/useLeadsForm';
import { GenericView, SearchInput, ActionButtons } from '../../components/common/table';
import api from '../../api';
import BusinessLocationAutocomplete from './components/CompanyEdit/BusinessLocationAutocomplete/BusinessLocationAutocomplete';
import Alert from '../../components/Alert/Alert';
import CompanyEdit from './components/CompanyEdit/CompanyEdit';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import { useAuth } from '../../contexts/AuthProvider';
import * as XLSX from 'xlsx';
import useModalEffect from '../../hooks/useModalEffect';
import { usePersistedFilters } from '../../hooks/usePersistedFilters';
import { Link } from 'react-router-dom';

const DEFAULT_PAGE_SIZE = 10;

const getEmptyCompanyFilters = () => ({
  dateFrom: '',
  dateTo: '',
  createdBy: [],
  stage: '',
  sector: [],
  source: [],
  service: [],
  department: [],
  branchId: [],
  taggingIds: [],
  standard: '',
  period: '',
  startDate: '',
  endDate: '',
  filterType: ''
});

// Map frontend column keys to backend sort property names (Spring sort param)
const PROSPECT_SORT_KEY_TO_BACKEND = {
  companyName: 'companyName',
  projectCount: 'projectCount',
  leadsCount: 'leadsCount',
  contactName: 'contactName',
  phone: 'phone',
  email: 'email',
  sector: 'sectorName',
  createdBy: 'createdByName',
  departmentName: 'departmentName',
  companyCreatedDate: 'companyCreatedDate',
  lastUpdateDate: 'companyUpdatedDate',
};

const Prospects = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [prospectData, setProspectData] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  /** Search term last applied via Enter; kept in sync with API so pagination/sort use the same filter. */
  const [appliedSearch, setAppliedSearch] = useState('');
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });

  // Server-side pagination (backend returns Page<T>)
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Server-side sort (ascending/descending handled by backend)
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const [showPassword, setShowPassword] = useState([]);
  const [showConfirmPassword, setShowConfirmPassword] = useState([]);

  // Use global auth context with capabilities
  const { user, can, capabilitiesLoading } = useAuth();
  
  // Helper function to check prospect-specific capabilities
  const canProspect = (capabilityKey) => can(capabilityKey, 'prospect');

  const formatDateDisplay = (dateValue) => {
    if (!dateValue) return '';
  
    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) return '';
  
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
  
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
  
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const togglePasswordVisibility = (index) => {
    setShowPassword(prev => {
      const copy = [...prev];
      copy[index] = !copy[index];
      return copy;
    });
  };

  const toggleConfirmPasswordVisibility = (index) => {
    setShowConfirmPassword(prev => {
      const copy = [...prev];
      copy[index] = !copy[index];
      return copy;
    });
  };

  const [partnerTypes, setPartnerTypes] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [entityTypes, setEntityTypes] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [userBranchesForFilter, setUserBranchesForFilter] = useState([]);
  const [taggingOptions, setTaggingOptions] = useState([]);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [standardsOptions, setStandardsOptions] = useState([]);
  const [allUsersWithDept, setAllUsersWithDept] = useState([]);

  const [filterData, setFilterData] = useState(getEmptyCompanyFilters);
  const [appliedFilters, setAppliedFilters] = useState(getEmptyCompanyFilters);

  const [filterOptions, setFilterOptions] = useState({
    createdByUsers: [],
    stages: [],
    sectors: [],
    sources: [],
    services: [],
    departments: []
  });

  const navigate = useNavigate();
  const { loadPersistedState, persistState } = usePersistedFilters('company');

  const {
    formData,
    formErrors,
    serviceSections,
    uploadedFiles,
    handleInputChange,
    handleTextChange,
    handleFormSubmit,
    addServiceSection,
    deleteServiceSection,
    onDrop,
    removeFile,
    resetForm,
    options,
    fetchContactOptions,
    standardsOptionsMap
  } = useLeadsForm();

  const [companyFormData, setCompanyFormData] = useState({
    branchId: '',
    contacts: [],
  });
  const [companyErrors, setCompanyErrors] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [modalDataLoading, setModalDataLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const [duplicateCompanies, setDuplicateCompanies] = useState([]);
  const [similarCompanies, setSimilarCompanies] = useState([]);
  const [isCheckingDuplicates, setIsCheckingDuplicates] = useState(false);

  // Handler untuk input
  const handleCompanyInputChange = (e) => {
    const { name, value } = e.target;
    setCompanyFormData(prev => ({ ...prev, [name]: value }));

    // Clear error when input changes (for optional fields)
    if (['companyName', 'phone', 'website', 'companyEmail', 'npwp', 'country', 'state', 'city', 'postalCode'].includes(name)) {
      setCompanyErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Handler untuk select
  const handleCompanySelectChange = (name, value) => {
    // Multi-select (taggingIds) passes an array
    if (name === 'taggingIds') {
      const arr = Array.isArray(value) ? value : (value ? [value] : []);
      const normalizedIds = arr.map(v => (v != null && v !== '') ? String(v) : null).filter(Boolean);
      setCompanyFormData(prev => ({ ...prev, [name]: normalizedIds }));
      if (normalizedIds.length > 0) setCompanyErrors(prev => ({ ...prev, [name]: undefined }));
      return;
    }
    setCompanyFormData(prev => ({ ...prev, [name]: value }));

    // Clear error when select changes
    if (value) {
      setCompanyErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Handler untuk kontak
  const addContact = () => {
    setCompanyFormData(prev => ({
      ...prev,
      contacts: [...(prev.contacts || []), { firstName: '', lastName: '', email: '', phone: '', position: '' }]
    }));
  };
  const removeContact = (index) => {
    setCompanyFormData(prev => ({
      ...prev,
      contacts: (Array.isArray(prev.contacts) ? prev.contacts : []).filter((_, i) => i !== index)
    }));
  };

  const getProspectListFilters = (filters = appliedFilters, search = appliedSearch) => {
    const q = (search ?? '').trim();
    if (q) return { ...filters, search: q };
    return filters;
  };

  const fetchProspects = async (filters = {}, pageNum = page, pageSize = size, sortConfigOverride = null) => {
    try {
      setTableLoading(true);
      setOverviewLoading(true); // Show loading for overview when fetching data
      
      // Build query parameters
      const params = new URLSearchParams();
      params.set('page', String(pageNum));
      params.set('size', String(pageSize));

      const effectiveDateFrom = filters.startDate || filters.dateFrom;
      const effectiveDateTo = filters.endDate || filters.dateTo;
      if (effectiveDateFrom) params.append('dateFrom', effectiveDateFrom);
      if (effectiveDateTo) params.append('dateTo', effectiveDateTo);

      if (Array.isArray(filters.branchId) && filters.branchId.length > 0) {
        filters.branchId
          .map(v => String(v).trim())
          .filter(Boolean)
          .forEach(v => params.append('branchId', v));
      }

      if (Array.isArray(filters.taggingIds) && filters.taggingIds.length > 0) {
        params.append('taggingIds', filters.taggingIds.map(v => String(v)).join(','));
      }

      ['stage', 'standard', 'filterType', 'search'].forEach((key) => {
        const value = filters[key];
        if (value && value !== '' && value !== 'all') {
          params.append(key, value);
        }
      });
      ['createdBy', 'sector', 'source', 'service', 'department'].forEach((key) => {
        const value = filters[key];
        if (Array.isArray(value) && value.length > 0) {
          params.append(key, value.map(v => String(v)).join(','));
        } else if (value && !Array.isArray(value) && value !== '' && value !== 'all') {
          params.append(key, value);
        }
      });
      // ✅ Permission-based filtering hierarchy:
      // 1. view_global → see all (no filter)
      // 2. view_department → filter by user's department
      // 3. view_own → filter by user ID (created by)
      
      if (!canProspect('view_global')) {
        if (canProspect('view_department')) {
          // Filter by department (unless user explicitly filters by createdBy)
          if (!params.has('createdBy') && user?.departmentId) {
            params.append('viewDepartmentId', user.departmentId);
          }
        } else if (canProspect('view_own')) {
          // Filter by user (created by)
          if (!params.has('createdBy') && user?.id) {
            params.append('createdBy', user.id);
          }
        }
      }
      // ✅ Server-side sort: backend expects sort=propertyName,direction (e.g. sort=companyName,asc)
      const effectiveSort = sortConfigOverride ?? sortConfig;
      if (effectiveSort?.key && PROSPECT_SORT_KEY_TO_BACKEND[effectiveSort.key]) {
        const backendSortProperty = PROSPECT_SORT_KEY_TO_BACKEND[effectiveSort.key];
        params.set('sort', `${backendSortProperty},${effectiveSort.direction}`);
      }
      // ✅ PERBAIKAN: Gunakan API baru untuk prospects (pagination: Page<T>)
      const hasFilters = [...params.keys()].some(k => k !== 'page' && k !== 'size' && k !== 'sort');
      const baseUrl = hasFilters ? `/crm/prospects/filter?${params.toString()}` : `/crm/prospects?${params.toString()}`;
      const res = await api.get(baseUrl);
      const data = res.data;
      // Backend returns Page<T>: { content, totalElements, totalPages, number, size, ... }
      const list = Array.isArray(data) ? data : (data?.content ?? []);
      setProspectData(list);
      if (data && typeof data.totalElements === 'number') {
        setTotalElements(data.totalElements);
        setTotalPages(data.totalPages ?? Math.max(1, Math.ceil(data.totalElements / pageSize)));
      } else {
        setTotalElements(list.length);
        setTotalPages(1);
      }
    } finally {
      setTableLoading(false);
      setOverviewLoading(false); // Hide loading for overview when data is loaded
    }
  };

  useEffect(() => {
    if (!capabilitiesLoading) {
      setTableLoading(true);
      setOverviewLoading(true);

      const saved = loadPersistedState();
      if (saved) {
        const restoredFilters = saved.filters ?? getEmptyCompanyFilters();
        const restoredSearch = saved.search ?? '';
        const restoredPage = saved.page ?? 0;
        const restoredSize = saved.size ?? DEFAULT_PAGE_SIZE;

        setAppliedFilters(restoredFilters);
        setFilterData(restoredFilters);
        setAppliedSearch(restoredSearch);
        setSearchQuery(restoredSearch);
        setPage(restoredPage);
        setSize(restoredSize);

        fetchProspects(
          getProspectListFilters(restoredFilters, restoredSearch),
          restoredPage,
          restoredSize,
          sortConfig
        );
      } else {
        fetchProspects(getProspectListFilters(), page, size, sortConfig);
      }
    }
  }, [capabilitiesLoading]);

  // Fetch initial data
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [partnerTypeRes, entityTypeRes, sectorRes, taggingRes, filterOptionsRes, createdByUsersRes, standardsRes, leadStatusesRes, mdSourcesRes, mdDepartmentsRes, internalUsersRes, servicesRes] = await Promise.all([
          api.get('/md/business-partner-type'),
          api.get('/md/entity-type'),
          api.get('/md/sector'),
          api.get('/md/tagging', { params: { moduleKey: 'companies' } }),
          api.get('/crm/prospects/filter-options').catch(() => ({ data: { createdByUsers: [], stages: [], sources: [], departments: [] } })),
          api.get('/crm/prospects/created-by-users').catch(() => ({ data: [] })),
          api.get('/dashboard/standards').catch(() => ({ data: { standards: [] } })),
          api.get('/crm/lead-statuses/options').catch(() => ({ data: [] })),
          api.get('/md/company-source').catch(() => ({ data: [] })),
          api.get('/md/departement').catch(() => ({ data: [] })),
          api.get('/md/internal-users/option-internal-user').catch(() => ({ data: [] })),
          api.get('/crm/services/options').catch(() => ({ data: [] }))
        ]);
        setAllUsersWithDept(internalUsersRes.data || []);
        setStandardsOptions(standardsRes.data?.standards || []);
        setPartnerTypes(partnerTypeRes.data);
        setEntityTypes(entityTypeRes.data);
        setSectors(sectorRes.data);
        setTaggingOptions(taggingRes.data || []);
        
        // ✅ PERBAIKAN: Use filter options dengan API baru untuk createdByUsers
        const filterOptionsData = filterOptionsRes.data || { createdByUsers: [], stages: [], sources: [], departments: [] };
        // ✅ TAMBAHAN: Gunakan API baru untuk createdByUsers
        let createdByUsers = [];
        
        // Prioritas 1: Gunakan data dari API baru /crm/prospects/created-by-users
        if (createdByUsersRes.data && createdByUsersRes.data.length > 0) {
          createdByUsers = createdByUsersRes.data;
        }
        // Prioritas 2: Gunakan data dari filter-options jika API baru kosong
        else if (filterOptionsData.createdByUsers && filterOptionsData.createdByUsers.length > 0) {
          createdByUsers = filterOptionsData.createdByUsers;
        }
        // Prioritas 3: Fallback ke array kosong jika tidak ada data
        else {
          createdByUsers = [];
        }
        
        // Set filter options dengan createdByUsers dari API baru
        setFilterOptions({
          createdByUsers: (createdByUsers || []).map(u => ({
            id: u.id || u.value,
            name: (u.firstName || u.lastName)
              ? `${u.firstName || ''} ${u.lastName || ''}`.trim()
              : (u.fullName || u.full_name || u.name || u.label || u.username)
          })),
          stages: (leadStatusesRes.data || []).map(s => ({ id: s.id || s.name, name: s.name })),
          sectors: (sectorRes.data || []).map(s => ({ value: s.id, label: s.name })),
          sources: (mdSourcesRes.data || []).map(s => ({ id: s.id || s.name, name: s.name })),
          services: (servicesRes.data || []).map(s => ({ value: s.id ?? s.value, label: s.name ?? s.label })),
          departments: (mdDepartmentsRes.data || []).map(d => ({ id: d.id, name: d.name }))
        });
        
      } catch (error) {
        console.error('Error fetching initial data:', error);
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    api.get('/branches')
      .then(res => {
        const raw = res.data?.data ?? res.data ?? [];
        const parsed = Array.isArray(raw) ? raw : (Array.isArray(raw?.content) ? raw.content : []);
        setBranches(parsed);
      })
      .catch(err => {
        console.warn('[branches] /branches failed:', err?.response?.status, err?.message);
        setBranches([]);
      });
  }, []);

  // Fetch current user's branches for filter (only show branches the user has access to)
  useEffect(() => {
    if (!user?.id) return;
    const loadUserBranches = async () => {
      try {
        const res = await api.get(`/user-branches/user/${user.id}`);
        const data = res.data?.data ?? res.data ?? [];
        const parsed = Array.isArray(data) ? data : [];
        setUserBranchesForFilter(parsed);
      } catch (err) {
        console.error('Error fetching user branches for filter:', err);
        setUserBranchesForFilter([]);
      }
    };
    loadUserBranches();
  }, [user?.id]);

  // Untuk menambah contact baru (safe when contacts is undefined, e.g. company from lead without contacts)
  const handleAddContact = () => {
    setCompanyFormData(prev => ({
      ...prev,
      contacts: [
        ...(Array.isArray(prev.contacts) ? prev.contacts : []),
        {
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          position: '',
          allowLogin: false,
          generatePassword: false,
          password: '',
          confirmPassword: ''
        }
      ]
    }));
  };

  // Untuk update field contact tertentu (safe when contacts is undefined)
  const handleContactInputChange = (index, field, value) => {
    const baseContacts = Array.isArray(companyFormData?.contacts) ? companyFormData.contacts : [];
    const updatedContacts = [...baseContacts];
    const existing = updatedContacts[index] ?? {};
    updatedContacts[index] = { ...existing, [field]: value };

    setCompanyFormData(prev => ({
      ...prev,
      contacts: updatedContacts
    }));
  };

  const checkCompanyNameDuplicates = async (companyName) => {
    if (!companyName || companyName.trim().length === 0) return;

    setIsCheckingDuplicates(true);
    try {
      const response = await api.get(`/md/companies/check-duplicates?name=${encodeURIComponent(companyName.trim())}`);
      const data = response.data;
      setDuplicateCompanies(data.exactDuplicates || []);
      setSimilarCompanies(data.similarCompanies || []);
    } catch (error) {
      console.error('Error checking for duplicate company names:', error);
      // Fallback when endpoint is unavailable.
      setDuplicateCompanies([]);
      setSimilarCompanies([]);
    } finally {
      setIsCheckingDuplicates(false);
    }
  };



  useEffect(() => {
    if (!showCompanyModal) return;
    const companyName = companyFormData?.companyName;

    if (companyName && companyName.trim().length > 2) {
      const timeoutId = setTimeout(() => {
        checkCompanyNameDuplicates(companyName);
      }, 500);
      return () => clearTimeout(timeoutId);
    }

    setDuplicateCompanies([]);
    setSimilarCompanies([]);
    setIsCheckingDuplicates(false);
  }, [showCompanyModal, companyFormData?.companyName]);

  const handleAddNew = (prospect) => {
    if (!canProspect('create')) {
      setAlert({ show: true, type: 'danger', message: 'You do not have permission to create leads.' });
      setTimeout(() => setAlert({ show: false, type: '', message: '' }), 3000);
      return;
    }
    // Reset form first
    resetForm();

    // Use setTimeout to ensure resetForm completes before setting new data
    setTimeout(() => {
      if (prospect) {
        // Find the correct ID field - try different possible field names
        const companyId = prospect.id || prospect.companyId || prospect._id || prospect.ID;

        // Set company ID as string to match dropdown value format
        if (companyId) {
          handleInputChange('company', String(companyId));
          handleInputChange('companyWebsite', prospect.website || '');
          handleInputChange('companyPhoneNumber', prospect.contactNumber || prospect.phone || '');
          handleInputChange('address', prospect.address || '');
          handleInputChange('sector', prospect.partnerType || prospect.sectorName || '');
          handleInputChange('contact', prospect.pic || prospect.contactName || '');
          handleInputChange('contactPhoneNumber', prospect.contactNumber || prospect.phone || '');
          handleInputChange('email', prospect.email || '');
          handleInputChange('stage', 'New');
          handleInputChange('projectName', '');
          handleInputChange('projectDescription', '');
          handleInputChange('leadSource', prospect.leadSource || '');

          // Fetch contacts for the selected company
          fetchContactOptions(String(companyId));
        } else {
          console.error('No company ID found in prospect data');
          // Try to find company by name in company options
          const companyByName = options.companyOptions?.find(c =>
            c.label === prospect.companyName || c.name === prospect.companyName
          );
          if (companyByName) {
            handleInputChange('company', String(companyByName.value));
            handleInputChange('companyWebsite', prospect.website || '');
            handleInputChange('companyPhoneNumber', prospect.contactNumber || prospect.phone || '');
            handleInputChange('address', prospect.address || '');
            handleInputChange('sector', prospect.partnerType || prospect.sectorName || '');
            handleInputChange('contact', prospect.pic || prospect.contactName || '');
            handleInputChange('contactPhoneNumber', prospect.contactNumber || prospect.phone || '');
            handleInputChange('email', prospect.email || '');
            handleInputChange('stage', 'New');
            handleInputChange('projectName', '');
            handleInputChange('projectDescription', '');
            handleInputChange('leadSource', prospect.leadSource || '');

            fetchContactOptions(String(companyByName.value));
          } else {
            console.error('Company not found by name either');
          }
        }
      }

      // Open modal after data is set
      setIsModalOpen(true);
    }, 100);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  // const handleCompanyClick = (companyName) => {
  //   navigate(`/clients/${encodeURIComponent(companyName)}`);
  // };

  const handleCompanyClick = (companyId) => {
    navigate(`/clients/${companyId}`);
  };

  // Handle search - only trigger on Enter key press (reset to page 0)
  const handleSearch = () => {
    setPage(0);
    const q = (searchQuery ?? '').trim();
    setAppliedSearch(q);
    const searchFilters = getProspectListFilters(appliedFilters, q);
    persistState({ filters: appliedFilters, search: q, page: 0, size });
    fetchProspects(searchFilters, 0, size, sortConfig);
  };

  // Handle search on Enter key
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // ✅ PERBAIKAN: Transform data dengan Created By dari gabungan tabel companies dan users
  // Expected API response structure:
  // - createdByName: nama user yang membuat company (dari JOIN companies.created_by = users.id)
  // - createdBy: ID user yang membuat company
  // - userName: nama user (fallback)
  // - user.name: object user dengan property name
  // - createdUser.name: object createdUser dengan property name
  // - createdByUser.name: object createdByUser dengan property name
  // - companyCreatedBy: nama user yang membuat company (alternative field)
  // - createdByUserName: nama user yang membuat company (alternative field)
  const transformedData = prospectData
    .map(item => ({
      ...item,
      projectCount: item.projectCount || 0,
      leadsCount: item.leadsCount || 0,
      contactName: item.contactName,
      phone: item.phone,
      sector: item.sectorName,
      email: item.email,
      assignedUser: item.assignedUserName || 'Unassigned',
      // ✅ TAGGING: Include tagging from company data (comma-separated string or taggings)
      tagging: item.tagging ?? item.company?.tagging ?? (item.taggings && Array.isArray(item.taggings) ? item.taggings.map(t => t.name).join(', ') : null) ?? (item.company?.taggings && Array.isArray(item.company.taggings) ? item.company.taggings.map(t => t.name).join(', ') : null) ?? '-',
      // ✅ PERBAIKAN: Created By dari gabungan tabel companies dan users dengan multiple fallback
      createdBy: item.createdByName || 
                 item.createdBy || 
                 item.userName || 
                 item.user?.name || 
                 item.createdUser?.name || 
                 item.createdByUser?.name || 
                 item.companyCreatedBy || 
                 item.createdByUserName || 
                 '-',
      departmentName: item.departmentName || '-',
      companyCreatedDate: item.companyCreatedDate || '-',
      // Last Update fields
      lastUpdateDate: item.lastUpdateDate || item.companyUpdatedDate || item.udate || '-',
      lastUpdateBy: item.lastUpdateByName || 
                   item.lastUpdateBy || 
                   item.updatedByName || 
                   item.updatedBy || 
                   item.updateUserName || 
                   item.updateUser?.name || 
                   item.updatedUser?.name || 
                   item.lastUpdatedByUserName || 
                   '-'
    }));

  const columns = [
    {
      key: 'companyName',
      label: 'Company Name',
      sortable: true,
      className: 'text-truncate',
      render: (value, row) => (
        <Link
          to={`/clients/${row.companyId}`}
          className="text-primary text-truncate d-inline-block text-decoration-none"
          style={{ maxWidth: '250px', width: '250px' }}
          title={value}
          aria-label={`View details for ${value}`}
        >
          {value}
        </Link>
      )
    },
    {
      key: 'tagging',
      label: 'Tagging',
      sortable: false,
      className: 'text-truncate',
      render: (value) => {
        const raw = !value ? '' : (typeof value === 'string' ? value : (typeof value === 'object' && value && value.name ? value.name : (taggingOptions.find(opt => String(opt.id) === String(value))?.name ?? value)));
        const tags = raw ? raw.split(',').map(s => s.trim()).filter(Boolean) : [];
        if (tags.length === 0) {
          return <span className="text-muted">-</span>;
        }
        return (
          <span className="d-inline-flex flex-wrap gap-1" style={{ maxWidth: '180px' }}>
            {tags.map((tag, i) => (
              <span
                key={i}
                className="badge bg-blue-lt text-wrap"
                title={tag}
                style={{ fontSize: '0.7rem' }}
              >
                {tag}
              </span>
            ))}
          </span>
        );
      }
    },
    { 
      key: 'projectCount', 
      label: 'Projects', 
      type: 'number',
      sortable: true,
      className: 'text-truncate',
      render: (value) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: '80px', width: '80px' }}
          title={value}
        >
          {value}
        </span>
      )
    },
    { 
      key: 'leadsCount', 
      label: 'Leads', 
      type: 'number',
      sortable: true,
      className: 'text-truncate',
      render: (value) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: '80px', width: '80px' }}
          title={value}
        >
          {value}
        </span>
      )
    },
    { 
      key: 'contactName', 
      label: 'Contact Name',
      sortable: true,
      className: 'text-truncate',
      render: (value) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: '200px', width: '200px' }}
          title={value}
        >
          {value}
        </span>
      )
    },
    { 
      key: 'phone', 
      label: 'Contact No.',
      sortable: true,
      className: 'text-truncate',
      render: (value) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: '150px', width: '150px' }}
          title={value}
        >
          {value}
        </span>
      )
    },
    { 
      key: 'email', 
      label: 'Contact Email',
      sortable: true,
      className: 'text-truncate',
      render: (value) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: '220px', width: '220px' }}
          title={value}
        >
          {value}
        </span>
      )
    },
    { 
      key: 'sector', 
      label: 'Sector',
      sortable: true,
      className: 'text-truncate',
      render: (value) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: '180px', width: '180px' }}
          title={value}
        >
          {value}
        </span>
      )
    },
    { 
      key: 'createdBy', 
      label: 'Created By',
      sortable: true,
      className: 'text-truncate',
      render: (value) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: '160px', width: '160px' }}
          title={value}
        >
          {value}
        </span>
      )
    },
    { 
      key: 'departmentName', 
      label: 'Department',
      sortable: true,
      className: 'text-truncate',
      render: (value) => (
        <span
          className="text-truncate d-inline-block"
          style={{ maxWidth: '150px', width: '150px' }}
          title={value}
        >
          {value}
        </span>
      )
    },
    { 
      key: 'companyCreatedDate', 
      label: 'Create Date',
      type: 'date',
      sortable: true,
      className: 'text-truncate',
      render: (value) => {
        if (!value || value === '-') return '-';
        const dateValue = formatDateDisplay(value);
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '140px', width: '140px' }}
            title={dateValue}
          >
            {dateValue || '-'}
          </span>
        );
      }
    },
    {
      key: 'lastUpdateDate',
      label: 'Last Update',
      type: 'date',
      sortable: true,
      className: 'text-truncate',
      render: (value) => {
        if (!value || value === '-') return '-';
        const dateValue = formatDateDisplay(value);
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '140px', width: '140px' }}
            title={dateValue}
          >
            {dateValue || '-'}
          </span>
        );
      }
    },
    {
      key: 'lastUpdateBy',
      label: 'Update By',
      sortable: false,
      className: 'text-truncate',
      render: (value) => {
        const displayValue = value || '-';
        return (
          <span
            className="text-truncate d-inline-block"
            style={{ maxWidth: '160px', width: '160px' }}
            title={displayValue}
          >
            {displayValue}
          </span>
        );
      }
    }
  ];

  const stats = {
    leads: { total: 56, new: 40, existing: 16 },
    hot: { total: 37, new: 20, existing: 17 },
    negotiation: { total: 12, new: 10, existing: 2 },
    converted: { total: 56, new: 40, existing: 16 }
  };

  // Build userId → { deptId, status } lookup from internal-users endpoint
  const companyUserDeptLookup = useMemo(() => {
    const map = new Map();
    (allUsersWithDept || []).forEach(u => {
      const deptId = u.departmentId || u.departementId;
      if (u.id != null) map.set(String(u.id), { deptId: deptId != null ? String(deptId) : null, status: u.status });
    });
    return map;
  }, [allUsersWithDept]);

  const withInactiveBadge = (userId, label) => {
    const entry = companyUserDeptLookup.get(String(userId));
    return entry?.status === '0' ? `${label} (Inactive)` : label;
  };

  // Cascade: filter createdBy options by selected department; mark inactive users
  const cascadedCompanyCreatedByOptions = useMemo(() => {
    return (filterOptions.createdByUsers || []).map(u => ({ value: u.id, label: withInactiveBadge(u.id, u.name) }));
  }, [filterOptions.createdByUsers]);

  const companyFilterFields = [
    { key: 'department', label: 'Department', type: 'multiselect', options: (filterOptions.departments || []).map(d => ({ value: d.id, label: d.name })) },
    { key: 'createdBy', label: 'Created By', type: 'multiselect', options: cascadedCompanyCreatedByOptions },
    { key: 'sector', label: 'Sector', type: 'multiselect', options: filterOptions.sectors || [] },
    { key: 'source', label: 'Source', type: 'multiselect', options: (filterOptions.sources || []).map(s => ({ value: s.id, label: s.name })) },
    { key: 'service', label: 'Service', type: 'multiselect', options: filterOptions.services || [] },
    { key: 'branchId', label: 'Branch', type: 'multiselect', options: (userBranchesForFilter || []).map(b => ({ value: String(b.id), label: b.name })) },
    { key: 'taggingIds', label: 'Tagging', type: 'multiselect', options: (taggingOptions || []).map(t => ({ value: String(t.id), label: t.name })) },
  ];

  const activeFilterCount = getActiveFilterCount(appliedFilters, companyFilterFields);
  const companyFilterTags = useMemo(() => {
    const tags = [];

    if (appliedFilters.period) {
      tags.push({
        key: 'period',
        label: `Period: ${String(appliedFilters.period).replace(/_/g, ' ')}`,
      });
    } else if (appliedFilters.startDate || appliedFilters.endDate) {
      tags.push({
        key: 'period',
        label: `Period: ${[appliedFilters.startDate, appliedFilters.endDate].filter(Boolean).join(' - ')}`,
      });
    }

    companyFilterFields.forEach((field) => {
      if (field.type === 'dateRange') {
        return;
      }
      const value = appliedFilters[field.key];
      const isEmpty = value === '' || value === null || value === undefined || (Array.isArray(value) && value.length === 0);
      if (isEmpty) {
        return;
      }

      if (Array.isArray(value)) {
        tags.push({ key: field.key, label: `${field.label}: ${value.length} selected` });
        return;
      }

      const option = (field.options || []).find((opt) => String(opt[field.valueField || 'value']) === String(value));
      tags.push({ key: field.key, label: `${field.label}: ${option ? option[field.labelField || 'label'] : value}` });
    });

    return tags;
  }, [appliedFilters, companyFilterFields]);

  const getClearedCompanyFilters = (filters, key) => {
    const keysToClear = ['period', 'startDate', 'endDate'].includes(key)
      ? ['period', 'startDate', 'endDate']
      : [key];
    const nextFilters = { ...filters };
    keysToClear.forEach((fieldKey) => {
      nextFilters[fieldKey] = ['branchId', 'taggingIds', 'department', 'createdBy', 'sector', 'source', 'service'].includes(fieldKey) ? [] : '';
    });
    return nextFilters;
  };

  const handleFilterChange = (name, value) => {
    if (['branchId', 'taggingIds', 'department', 'createdBy', 'sector', 'source', 'service'].includes(name)) {
      const arr = Array.isArray(value) ? value : (value ? [value] : []);
      setFilterData(prev => ({ ...prev, [name]: arr }));
      return;
    }
    setFilterData(prev => ({ ...prev, [name]: value }));
  };

  const handleFilterReset = () => {
    const emptyFilters = getEmptyCompanyFilters();
    setFilterData(emptyFilters);
    setAppliedFilters(emptyFilters);
    setSearchQuery('');
    setAppliedSearch('');
    setPage(0);
    persistState({ filters: emptyFilters, search: '', page: 0, size });
    fetchProspects({}, 0, size, sortConfig);
  };

  const handleFilterApply = () => {
    const { startDate, endDate } = getActivePeriodRange(filterData);
    const applied = { ...filterData, startDate, endDate };
    setAppliedFilters(applied);
    setPage(0);
    persistState({ filters: applied, search: appliedSearch, page: 0, size });
    fetchProspects(getProspectListFilters(applied, appliedSearch), 0, size, sortConfig);
  };

  const handleCompanyChipRemove = (key) => {
    const keysToCheck = ['period', 'startDate', 'endDate'].includes(key)
      ? ['period', 'startDate', 'endDate']
      : [key];
    const hasActiveValue = keysToCheck.some((fieldKey) => {
      const value = appliedFilters[fieldKey];
      return Array.isArray(value) ? value.length > 0 : Boolean(value);
    });
    if (!hasActiveValue) return;

    const nextAppliedFilters = getClearedCompanyFilters(appliedFilters, key);
    setFilterData(prev => getClearedCompanyFilters(prev, key));
    setAppliedFilters(nextAppliedFilters);
    setPage(0);
    persistState({ filters: nextAppliedFilters, search: appliedSearch, page: 0, size });
    fetchProspects(getProspectListFilters(nextAppliedFilters, appliedSearch), 0, size, sortConfig);
  };

  const handleTabChange = (tabId) => {
    if (tabId === 'tabs-list-data') {
      setTableLoading(true);
      // Simulate loading time for tab switch
      setTimeout(() => {
        setTableLoading(false);
      }, 500);
    }
    // Removed overview tab loading since data is already loaded on page load
  };

  const handleEditLead = (lead) => {
    if (!canProspect('edit')) {
      setAlert({ show: true, type: 'danger', message: 'You do not have permission to edit.' });
      setTimeout(() => setAlert({ show: false, type: '', message: '' }), 3000);
      return;
    }
    fetchCompanyDetail(lead.companyId);
  };

  const handleDeleteLead = (lead) => {
    if (!canProspect('delete')) {
      setAlert({ show: true, type: 'danger', message: 'You do not have permission to delete.' });
      setTimeout(() => setAlert({ show: false, type: '', message: '' }), 3000);
      return;
    }
    setDeleteTarget(lead);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    
    try {
      setTableLoading(true);
      await api.delete('/md/companies', {
        data: [deleteTarget.companyId] // atau deleteTarget.companyId, sesuaikan field ID-nya
      });
      // Refresh current page after delete
      fetchProspects(getProspectListFilters(), page, size, sortConfig);
      setAlert({
        show: true,
        type: 'success',
        message: 'Lead deleted successfully!'
      });
      // Hide alert after 3 seconds
      setTimeout(() => setAlert({ show: false, type: '', message: '' }), 3000);
    } catch (err) {
      console.error('Error deleting lead:', err);
      setAlert({
        show: true,
        type: 'danger',
        message: 'Failed to delete lead. Please try again.'
      });
      // Hide alert after 3 seconds
      setTimeout(() => setAlert({ show: false, type: '', message: '' }), 3000);
    } finally {
      setTableLoading(false);
      setShowDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  const handleCreateNewCompany = () => {
    if (!canProspect('create')) {
      setAlert({ show: true, type: 'danger', message: 'You do not have permission to create companies.' });
      setTimeout(() => setAlert({ show: false, type: '', message: '' }), 3000);
      return;
    }

    // Reset form data for new company
    setCompanyFormData({
      branchId: '',
      taggingIds: [],
      contacts: [{
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        position: '',
        allowLogin: false,
        generatePassword: false,
        password: '',
        confirmPassword: ''
      }]
    });
    setCompanyErrors({});
    setDuplicateCompanies([]);
    setSimilarCompanies([]);
    setIsCheckingDuplicates(false);
    setEditingId(null); // null indicates we're creating, not editing
    setShowCompanyModal(true);
  };

  const handleExportExcel = () => {
    if (!canProspect('export_data')) {
      setAlert({ show: true, type: 'danger', message: 'You do not have permission to export.' });
      setTimeout(() => setAlert({ show: false, type: '', message: '' }), 3000);
      return;
    }
    try {
      // Prepare data for Excel export
      const excelData = transformedData.map(row => ({
        'Company Name': row.companyName || '',
        'Projects': row.projectCount || 0,
        'Leads': row.leadsCount || 0,
        'Contact Name': row.contactName || '',
        'Contact No.': row.phone || '',
        'Contact Email': row.email || '',
        'Sector': row.sector || '',
        'Tagging': row.tagging || '-',
        'Created By': row.createdBy || '',
        'Department': row.departmentName || '',
        'Create Date': formatDateDisplay(row.companyCreatedDate) || '-',
        'Last Update': formatDateDisplay(row.lastUpdateDate) || '-',
        'Update By': row.lastUpdateBy || '-'
      }));

      // Create worksheet from data
      const worksheet = XLSX.utils.json_to_sheet(excelData);

      // Set column widths for better readability
      const columnWidths = [
        { wch: 30 }, // Company Name
        { wch: 10 }, // Projects
        { wch: 10 }, // Leads
        { wch: 25 }, // Contact Name
        { wch: 15 }, // Contact No.
        { wch: 30 }, // Contact Email
        { wch: 20 }, // Sector
        { wch: 15 }, // Tagging
        { wch: 20 }, // Created By
        { wch: 20 }, // Department
        { wch: 15 }, // Create Date
        { wch: 15 }, // Last Update
        { wch: 20 }  // Update By
      ];
      worksheet['!cols'] = columnWidths;

      // Create workbook and add the worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Companies');

      // Generate file name with timestamp
      const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const fileName = `companies_export_${timestamp}.xlsx`;

      // Write and download the file
      XLSX.writeFile(workbook, fileName);

      // Show success message
      setAlert({
        show: true,
        type: 'success',
        message: 'Excel file exported successfully!'
      });
      setTimeout(() => setAlert({ show: false, type: '', message: '' }), 3000);
    } catch (e) {
      console.error('Export Excel failed:', e);
      setAlert({
        show: true,
        type: 'danger',
        message: 'Failed to export Excel file. Please try again.'
      });
      setTimeout(() => setAlert({ show: false, type: '', message: '' }), 3000);
    }
  };

  const handleExportPDF = () => {
    if (!canProspect('export_data')) {
      setAlert({ show: true, type: 'danger', message: 'You do not have permission to export.' });
      setTimeout(() => setAlert({ show: false, type: '', message: '' }), 3000);
      return;
    }
    try {
      const win = window.open('', '_blank');
      if (!win) return;
      const style = `
        <style>
          body { font-family: Arial, sans-serif; padding: 16px; }
          h2 { margin: 0 0 12px 0; }
          table { border-collapse: collapse; width: 100%; }
          th, td { border: 1px solid #ccc; padding: 8px; text-align: left; font-size: 12px; }
          th { background: #f5f5f5; }
        </style>
      `;
      const header = ['Company Name','Projects','Leads','Contact Name','Contact No.','Contact Email','Sector','Tagging','Created By','Department','Create Date'];
      const rows = transformedData.map(r => [
        r.companyName,
        r.projectCount,
        r.leadsCount,
        r.contactName,
        r.phone,
        r.email,
        r.sector,
        r.tagging || '-',
        r.createdBy,
        r.departmentName,
        formatDateDisplay(r.companyCreatedDate) || '-'
      ]);
      const table = `
        <table>
          <thead>
            <tr>${header.map(h=>`<th>${h}</th>`).join('')}</tr>
          </thead>
          <tbody>
            ${rows.map(cols => `<tr>${cols.map(c => `<td>${c != null ? String(c) : ''}</td>`).join('')}</tr>`).join('')}
          </tbody>
        </table>
      `;
      win.document.write(`<!doctype html><html><head><meta charset="utf-8">${style}<title>Companies Export</title></head><body><h2>Companies Export</h2>${table}</body></html>`);
      win.document.close();
      win.focus();
      win.print();
    } catch (e) {
      console.error('Export PDF failed:', e);
    }
  };

  const handleContactLoginToggle = (index, value) => {
    const baseContacts = Array.isArray(companyFormData?.contacts) ? companyFormData.contacts : [];
    const updatedContacts = [...baseContacts];
    updatedContacts[index] = { ...(updatedContacts[index] ?? {}), allowLogin: value };
    if (!value) {
      updatedContacts[index].generatePassword = false;
      updatedContacts[index].password = '';
      updatedContacts[index].confirmPassword = '';
    }

    setCompanyFormData(prev => ({
      ...prev,
      contacts: updatedContacts,
    }));
  };


  const fetchCompanyDetail = async (id) => {
    try {
      setModalDataLoading(true);
      setShowCompanyModal(true);
      
      const res = await api.get(`/md/companies/${id}`);
      const data = res.data;
      const companyData = data.company ?? {};
      companyData.contacts = Array.isArray(data?.contacts) ? data.contacts : [];

      // Handle taggings: company detail returns taggings array [{ id, name }, ...]
      if (companyData.taggings && Array.isArray(companyData.taggings)) {
        companyData.taggingIds = companyData.taggings.map(t => String(t.id));
      } else if (companyData.tagging && typeof companyData.tagging === 'string' && taggingOptions.length > 0) {
        // Fallback: single comma-separated string from list API
        const names = companyData.tagging.split(',').map(s => s.trim()).filter(Boolean);
        companyData.taggingIds = names.map(n => {
          const found = taggingOptions.find(opt => opt.name === n || String(opt.id) === n);
          return found ? String(found.id) : null;
        }).filter(Boolean);
      } else {
        companyData.taggingIds = [];
      }
      delete companyData.clientFlagging;
      delete companyData.clientFlaggingId;
      delete companyData.taggings;

      companyData.branchId = companyData.branchId != null ? String(companyData.branchId) : (companyData.branch?.id != null ? String(companyData.branch.id) : '');

      setCompanyFormData(companyData);
      setCompanyErrors({}); // Clear any previous errors
      setDuplicateCompanies([]);
      setSimilarCompanies([]);
      setIsCheckingDuplicates(false);
      setEditingId(id);
    } catch (error) {
      console.error('Failed to load company details:', error);
      const msg = error?.response?.data?.message ?? error?.message ?? 'Failed to load company details. The company may not exist or you may not have access.';
      setAlert({ show: true, type: 'danger', message: msg });
      setTimeout(() => setAlert({ show: false, type: '', message: '' }), 5000);
      setShowCompanyModal(false);
    } finally {
      setModalDataLoading(false);
    }
  };

  const handleSubmitCompany = async (e) => {
    e.preventDefault();

    const isCreating = !editingId;

    if (isCreating && !canProspect('create')) {
      setAlert({ show: true, type: 'danger', message: 'You do not have permission to create companies.' });
      setTimeout(() => setAlert({ show: false, type: '', message: '' }), 3000);
      return;
    }

    if (!isCreating && !canProspect('edit')) {
      setAlert({ show: true, type: 'danger', message: 'You do not have permission to update companies.' });
      setTimeout(() => setAlert({ show: false, type: '', message: '' }), 3000);
      return;
    }

    const hasExactDuplicate = duplicateCompanies.some((company) => {
      const existingId = company?.id ?? company?.companyId;
      if (!editingId) return true;
      return String(existingId) !== String(editingId);
    });
    if (hasExactDuplicate) {
      setCompanyErrors(prev => ({
        ...prev,
        companyName: 'A company with this name already exists. Please choose a different name.'
      }));
      return;
    }

    // Branch is required when creating a company
    if (isCreating) {
      const branchId = companyFormData.branchId;
      if (branchId === undefined || branchId === null || String(branchId).trim() === '') {
        setCompanyErrors(prev => ({ ...prev, branchId: 'Branch is required' }));
        return;
      }
    }

    if (!companyFormData.phone || String(companyFormData.phone).trim() === '') {
      setCompanyErrors(prev => ({ ...prev, phone: 'Phone is required' }));
      return;
    }

    // Every contact needs an email — required by the backend regardless of allowLogin
    const contacts = Array.isArray(companyFormData.contacts) ? companyFormData.contacts : [];
    const missingEmailIndex = contacts.findIndex(
      (c) => !c?.email || String(c.email).trim() === ''
    );
    if (missingEmailIndex !== -1) {
      setAlert({
        show: true,
        type: 'danger',
        message: `Email is required for contact #${missingEmailIndex + 1}.`
      });
      setTimeout(() => setAlert({ show: false, type: '', message: '' }), 5000);
      return;
    }

    setCompanyErrors({});

    setLoading(true);

    const taggingIdsAsNumbers = (companyFormData.taggingIds && Array.isArray(companyFormData.taggingIds))
      ? companyFormData.taggingIds.map(id => Number(id)).filter(n => !isNaN(n))
      : [];
    const payload = { ...companyFormData, taggingIds: taggingIdsAsNumbers };

    try {
      if (isCreating) {
        // Create new company
        await api.post('/md/companies', payload);

        // Show success alert
        setAlert({
          show: true,
          type: 'success',
          message: 'Company created successfully!'
        });
      } else {
        // Update existing company
        await api.put(`/md/companies/${editingId}`, payload);

        // Show success alert
        setAlert({
          show: true,
          type: 'success',
          message: 'Company updated successfully!'
        });
      }

      // Reset form and states (modal is React-controlled via showCompanyModal)
      setShowCompanyModal(false);
      setCompanyFormData({
        branchId: '',
        contacts: []
      });
      setCompanyErrors({}); // Clear any previous errors
      setDuplicateCompanies([]);
      setSimilarCompanies([]);
      setIsCheckingDuplicates(false);

            // Refresh data
      fetchProspects(getProspectListFilters(), page, size, sortConfig);

      // Hide alert after 3 seconds
      setTimeout(() => {
        setAlert({ show: false, type: '', message: '' });
      }, 3000);

    } catch (error) {
      console.error(`Failed to ${isCreating ? 'create' : 'update'} company:`, error);
      let message = error?.response?.data?.message ?? error?.response?.data?.error;
      if (typeof message !== 'string') message = null;
      if (message && /constraint|ConstraintViolation|could not execute batch|foreign key|fk\d|nested exception/i.test(message)) {
        message = 'This contact cannot be removed because it is linked to one or more leads or other records. Reassign or remove those links first, then try again.';
      }
      setAlert({
        show: true,
        type: 'danger',
        message: message || `Failed to ${isCreating ? 'create' : 'update'} company. Please try again.`
      });

      setTimeout(() => setAlert({ show: false, type: '', message: '' }), 5000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-xl">
      {alert.show && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert({ show: false, type: '', message: '' })}
        />
      )}
      <div className="row g-2 align-items-center">
        <div className="col">
          {/* <h3 className="page-subtitle m-0">Overview</h3> */}
          <h2 className="page-title">Dashboard Company</h2>
        </div>
        <div className="col-auto ms-auto d-print-none">
          <div className="btn-list d-flex flex-wrap gap-2">
            {canProspect('create') && (
              <>
                <a href="#" className="btn btn-outline-primary" onClick={() => handleCreateNewCompany()} aria-label="Create new company">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    className="icon icon-2" aria-hidden="true">
                    <path d="M12 5l0 14"></path>
                    <path d="M5 12l14 0"></path>
                  </svg>
                  New Company
                </a>
                <a href="#" className="btn btn-primary" onClick={() => handleAddNew(null)} aria-label="Create new lead">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    className="icon icon-2" aria-hidden="true">
                    <path d="M12 5l0 14"></path>
                    <path d="M5 12l14 0"></path>
                  </svg>
                  New Leads
                </a>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="page-body">
        <div className="container-xl">
          <div className="col">
            <div className="card">
              <div className="card-header">
                <ul className="nav nav-tabs card-header-tabs" data-bs-toggle="tabs" role="tablist" aria-label="Company dashboard tabs">
                  {/* <li className="nav-item-tab" role="presentation">
                    <a href="#tabs-overview" className="nav-link active" data-bs-toggle="tab" role="tab" aria-selected="true" aria-controls="tabs-overview" onClick={() => handleTabChange('tabs-overview')}>Overview</a>
                  </li> */}
                  <li className="nav-item-tab" role="presentation">
                    <a href="#tabs-list-data" className="nav-link active" data-bs-toggle="tab" role="tab" aria-selected="true" aria-controls="tabs-list-data" onClick={() => handleTabChange('tabs-list-data')}>List Data</a>
                  </li>
                </ul>
              </div>
              <div className="card-body">
                <div className="tab-content">
                  {/* <div className="tab-pane active show" id="tabs-overview" role="tabpanel" aria-labelledby="tabs-overview">
                    <OverviewView stats={stats} isLoading={overviewLoading} />
                  </div> */}
                  <div className="tab-pane active show" id="tabs-list-data" role="tabpanel" aria-labelledby="tabs-list-data">
                    <GenericView
                      data={transformedData}
                      columns={columns}
                      onEdit={canProspect('edit') ? handleEditLead : undefined}
                      onDelete={canProspect('delete') ? handleDeleteLead : undefined}
                      onAdd={canProspect('create') ? handleAddNew : undefined}
                      filterComponent={
                        <FilterButton offcanvasId="offcanvasCompany" activeCount={activeFilterCount} />
                      }
                      searchComponent={
                        <SearchInput
                          value={searchQuery}
                          onChange={setSearchQuery}
                          onKeyPress={handleSearchKeyPress}
                          placeholder="Search Companies"
                          id="companies-search"
                          autocomplete="off"
                        />
                      }
                      actionButtons={
                        <ActionButtons
                          onExportExcel={canProspect('export_data') ? handleExportExcel : undefined}
                          onExportPDF={canProspect('export_data') ? handleExportPDF : undefined}
                        />
                      }
                      belowTopControlsContent={
                        <FilterAwarenessBar
                          title="Companies is showing a filtered view"
                          summary={`Filtered by ${activeFilterCount} ${activeFilterCount === 1 ? 'criterion' : 'criteria'}`}
                          detail={companyFilterTags.map((tag) => tag.label).join(' · ')}
                          loading={tableLoading || capabilitiesLoading}
                          activeCount={activeFilterCount}
                          filterTags={companyFilterTags}
                          onRemoveFilter={handleCompanyChipRemove}
                          onResetFilters={handleFilterReset}
                        />
                      }
                      checkboxSelection={true}
                      onRowClick={(item) => {}}
                      emptyStateMessage="No prospects found"
                      isLoading={tableLoading || capabilitiesLoading}
                      pagination={{
                        currentPage: page + 1,
                        totalRows: totalElements,
                        rowsPerPage: size,
                        onPageChange: (newPage) => {
                          const nextPage = newPage - 1;
                          setPage(nextPage);
                          persistState({ filters: appliedFilters, search: appliedSearch, page: nextPage, size });
                          fetchProspects(getProspectListFilters(), nextPage, size, sortConfig);
                        },
                        onRowsPerPageChange: (newSize) => {
                          setSize(newSize);
                          setPage(0);
                          persistState({ filters: appliedFilters, search: appliedSearch, page: 0, size: newSize });
                          fetchProspects(getProspectListFilters(), 0, newSize, sortConfig);
                        },
                      }}
                      onSortChange={(newSortConfig) => {
                        setSortConfig(newSortConfig);
                        setPage(0);
                        fetchProspects(getProspectListFilters(), 0, size, newSortConfig);
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Offcanvas */}
      <AdvancedFilterPanel
        offcanvasId="offcanvasCompany"
        title="Advanced Filter"
        filterData={filterData}
        onChange={handleFilterChange}
        onApply={handleFilterApply}
        onReset={handleFilterReset}
        filterFields={companyFilterFields}
      />

      <LeadsForm
        formData={formData}
        formErrors={formErrors}
        handleInputChange={handleInputChange}
        handleTextChange={handleTextChange}
        handleFormSubmit={handleFormSubmit}
        serviceSections={serviceSections}
        addServiceSection={addServiceSection}
        deleteServiceSection={deleteServiceSection}
        uploadedFiles={uploadedFiles}
        onDrop={onDrop}
        removeFile={removeFile}
        options={options}
        isOpen={isModalOpen}
        onClose={handleModalClose}
        fetchContactOptions={fetchContactOptions}
        standardsOptionsMap={standardsOptionsMap}
        onSuccessSubmit={(error) => {
          if (error) {
            // Show error notification
            setAlert({
              show: true,
              type: 'danger',
              message: 'Failed to create lead. Please try again.'
            });
            // Hide alert after 3 seconds
            setTimeout(() => {
              setAlert({ show: false, type: '', message: '' });
            }, 3000);
          } else {
            // Show success notification
            setAlert({
              show: true,
              type: 'success',
              message: 'New lead created successfully!'
            });
            // Hide alert after 3 seconds
            setTimeout(() => {
              setAlert({ show: false, type: '', message: '' });
            }, 3000);
            // Refresh prospects data (first page)
            setPage(0);
            fetchProspects(getProspectListFilters(), 0, size, sortConfig);
          }
          setIsModalOpen(false);
        }}
      />

      <CompanyEdit
        isOpen={showCompanyModal}
        onClose={() => setShowCompanyModal(false)}
        onSubmit={handleSubmitCompany}
        editingId={editingId}
        companyFormData={companyFormData}
        companyErrors={companyErrors}
        loading={loading}
        modalDataLoading={modalDataLoading}
        handleCompanyInputChange={handleCompanyInputChange}
        handleCompanySelectChange={handleCompanySelectChange}
        handleContactInputChange={handleContactInputChange}
        handleContactLoginToggle={handleContactLoginToggle}
        handleAddContact={handleAddContact}
        removeContact={removeContact}
        partnerTypes={partnerTypes}
        entityTypes={entityTypes}
        sectors={sectors}
        currencies={currencies}
        branches={(branches.length > 0 ? branches : userBranchesForFilter).map(b => ({
            id: b.id ?? b.value ?? b.branchId ?? b.branch?.id,
            name: b.name ?? b.branchName ?? b.label ?? b.branch?.name ?? ''
          })).filter(b => b.id != null)}
        taggingOptions={taggingOptions}
        assignedUserOptions={(assignedUsers || []).map(u => ({ id: u.value || u.id, name: u.label || u.name }))}
        duplicateCompanies={duplicateCompanies}
        similarCompanies={similarCompanies}
        isCheckingDuplicates={isCheckingDuplicates}
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeleteTarget(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Lead"
        message={`Are you sure you want to delete this? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        size="md"
      />
    </div>
  );
};

export default Prospects;
 